import _, { set } from 'lodash';
import styles from './styles.module.scss';
import React, { useContext, useEffect, useMemo } from 'react';
import { DealRoomContext } from '../../components/context';
import { TabBar } from 'src/components/tabbar';
import cx from 'classnames';
import { useSpring, animated } from '@react-spring/web';
import InfiniteScroll from 'src/components/infiniteScroll';
import Actions from 'src/components/actionbar';
import { useSelector } from 'react-redux';
import { DealRoom, StakeHolderTypeEnum } from 'src/types/dealRoom';
import Slider from 'src/components/slider';
import { useDispatch } from 'react-redux';
import { deleteStakeholder, fetchStakeholders } from 'src/redux/actions/dealRoom';
import Button from 'src/components/button';
import { CollaborateDialog } from '../../components/collaborate';
import { useNavigate, useParams } from 'react-router-dom';
import ComposeDialog from 'src/components/compose/components/dialog';
import userRoleService from 'src/services/userRoles.service';
import Tooltip from 'src/components/tooltip';
 
const DefaultProfilePicture = "/images/default_user_profile_background.png";

const PeoplePage: React.FC = () => {

  const dispatch = useDispatch();
  const { type } = useParams();
  const navigate = useNavigate();

  const { id, dealRoom, onMenuChange, menuState, handleShareClick, hasContents } = useContext(DealRoomContext);
  const editing = menuState.primaryAction === 'edit';
  const [activeTab, setActiveTab] = React.useState(0)

  const [deleting, setDeleting] = React.useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = React.useState(null);
  const [showCollaborate, setShowCollaborate] = React.useState(false);

  const account = useSelector((state) => _.get(state, 'accounts.item', {}));

  const internalStakeholders = useSelector((state) => _.get(state, `dealRooms.dealRoom[${id}].stakeholders.people.[${StakeHolderTypeEnum.INTERNAL}`, {}));
  const externalStakeholders = useSelector((state) => _.get(state, `dealRooms.dealRoom[${id}].stakeholders.people.[${StakeHolderTypeEnum.EXTERNAL}`, {}));

  const stakeholderDelete = useSelector((state) => _.get(state, `dealRooms.dealRoom[${id}].stakeholders.deletion`, {}));
  const isStakeholderDeleting = _.get(stakeholderDelete, "deleting", false);
  const isStakeholderDeleted = _.get(stakeholderDelete, "deleted", false);


  useEffect(() => {
    if (type === "external") {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [type])

  useEffect(() => {

    if (!isStakeholderDeleting && isStakeholderDeleted) {
      setDeleting(false);
      setSelectedStakeholder(null);
      fetchStakeholdersForTab(0);
    }

  }, [isStakeholderDeleted, isStakeholderDeleting])

  const handleDeleteStakeholderClick = (item) => {
    setDeleting(true);
    setSelectedStakeholder(item)
  }

  const handleDeleteStakeholder = (stakeholderType: StakeHolderTypeEnum) => {
    dispatch(deleteStakeholder({
      stakeholderType: stakeholderType,
      dealRoomId: id || "",
      recipientId: _.get(selectedStakeholder, "shareRecipient", "")
    }))
  }

  const fetchStakeholdersForTab = (page: number) => {
    if (activeTab === 0) {
      dispatch(fetchStakeholders({
        dealRoomId: id || "",
        type: StakeHolderTypeEnum.INTERNAL,
        page: page
      }))
    }
    else {
      dispatch(fetchStakeholders({
        dealRoomId: id || "",
        type: StakeHolderTypeEnum.EXTERNAL,
        page: page
      }))
    }
  }

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === 1) {
      navigate(`/deal-room/${id}/people/external`)
    }
    else {
      navigate(`/deal-room/${id}/people/internal`)
    }
  }

  return (
    <div className={styles.peopleContainer}>
      <TabBar
        className={cx(styles.tabs)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={handleTabClick}
      >
        <div style={{ maxWidth: '300px' }} className={cx(styles.tab, styles.truncatedText, { [styles.active]: activeTab == 0 })}>
          {_.get(account,"name","")}
        </div>
        <div style={{ maxWidth: '300px' }} className={cx(styles.tab, styles.truncatedText, { [styles.active]: activeTab == 1 })}>
          {_.size(_.get(dealRoom, "customerName")) === 0 ? "Buyer Name" : dealRoom.customerName}
          <Tooltip content={'You can change the buyer name on the banner'} placement='right'>
              <i className={cx("paperflite-info",styles.infoIcon)}></i>
          </Tooltip>
        </div>
      </TabBar>

      <div className={cx(styles.stacks, styles.tabContainer)}>
        {userRoleService.canCollaborateDealRoom() && (type === "internal" ? _.get(dealRoom,"canEdit", false) : true) && <Actions.Button
          width={4}
          className={styles.manage}
          icon={type === "internal" ? "paperflite-user-settings-property" : "paperflite-mail"}
          title={type === "internal" ? "manage stakeholders" : "invite stakeholders"}
          onClick={() => activeTab === 0 ? setShowCollaborate(true) : handleShareClick()}
          disabled={activeTab === 1 ? !hasContents : false}
        />}
        <TabSection index={0} selected={activeTab}>
          <div className={styles.vendor}>
            <p className={styles.header}>{`Internal Stakeholders (${_.get(internalStakeholders, "pagination.totalRecordsCount", 0)})`}</p>
            <p className={styles.subText}>Invite internal users to give them access to this room</p>
            {_.size(_.get(internalStakeholders, "items", [])) > 0 && <InfiniteScroll className={styles.usersList}
              loading={_.get(internalStakeholders, "loading", false)}
              hasMore={_.get(internalStakeholders, "pagination.hasMoreItems", false)}
              loadMore={() => {
                const nextPage = _.get(internalStakeholders, "pagination.currentPage", 0) + 1;
                if (_.get(internalStakeholders, "loading", false)) return;
                fetchStakeholdersForTab(nextPage);
              }}
              loader={() => {
                return (
                  <PlaceholderCard />
                )
              }}
            >
              {_.get(internalStakeholders, "items", []).map((item) => (
                <PeopleCard editing={false} item={item} handleDeleteClick={handleDeleteStakeholderClick} showActivityStatus={false} />
              ))}
            </InfiniteScroll>}
          </div>
        </TabSection>
        <TabSection index={1} selected={activeTab}>
          <div className={styles.customer}>
            <p className={styles.header}>{`External Stakeholders (${_.get(externalStakeholders, "pagination.totalRecordsCount", 0)})`}</p>
            <p className={styles.subText}>Invite external users to give them access to this room</p>
            {_.size(_.get(externalStakeholders, "items", [])) === 0 && !editing && <div className={styles.emptyList}>
              <PlaceholderCard animate={false} />
              <div className={cx(styles.peopleCard, styles.noPeople)}>
                <p>No people added</p>
              </div>

            </div>}
            {_.size(_.get(externalStakeholders, "items", [])) > 0 && <InfiniteScroll className={styles.usersList}
              loading={_.get(externalStakeholders, "loading", false)}
              hasMore={_.get(externalStakeholders, "pagination.hasMoreItems", false)}
              loadMore={() => {
                const nextPage = _.get(externalStakeholders, "pagination.currentPage", 0) + 1;
                if (_.get(externalStakeholders, "loading", false)) return;
                fetchStakeholdersForTab(nextPage);
              }}
              loader={() => {
                return (
                  <PlaceholderCard />
                )
              }}
            >
              {_.get(externalStakeholders, "items", []).map((item) => (
                <PeopleCard editing={editing} item={item} handleDeleteClick={handleDeleteStakeholderClick} showActivityStatus={false} />
              ))}
            </InfiniteScroll>}
          </div>
        </TabSection>

        <Slider.Dialog
          width="300"
          show={deleting}
          onClose={() => {
            setDeleting(false)
          }}>
          <Slider.Header title="removing" message="1 stakeholder">
            <p className={styles.description}>
              If you remove this stakeholder, they won't have access to the deal anymore. They can rejoin, but only with a new invite from you.
            </p>
          </Slider.Header>
          <Slider.Body>
            <PeopleCard editing={false} item={selectedStakeholder} />
          </Slider.Body>
          <Slider.Actions>
            <Button variant="primary"
              icon="fa-solid fa-check"
              text={isStakeholderDeleting ? "Removing" : "Remove"}
              disabled={isStakeholderDeleting}
              onClick={() => handleDeleteStakeholder(StakeHolderTypeEnum.EXTERNAL)}
            />
            <Button text="Cancel"
              disabled={isStakeholderDeleting}
              onClick={() => {
                setDeleting(false)
                setSelectedStakeholder(null)
              }} />
          </Slider.Actions>
        </Slider.Dialog>

        <CollaborateDialog show={showCollaborate}
          dealRoom={dealRoom as DealRoom}
          onClose={() => setShowCollaborate(false)}
        />
      </div>
    </div>
  )
};

const PlaceholderCard = ({ animate = true }) => {
  return (
    <div className={cx(styles.peopleCard, styles.placeholderCard)}>
      <div className={cx(styles.avatar, {
        [styles.block]: animate,
        [styles.static]: !animate
      })} />
      <div className={styles.details}>
        <p className={cx(styles.name, {
          [styles.block]: animate,
          [styles.static]: !animate
        })} />
        <p className={cx(styles.email, styles.loadingCard, {
          [styles.block]: animate,
          [styles.static]: !animate
        })} />
      </div>
    </div>
  )
}

interface PeopleCardProps {
  item: any;
  editing: boolean;
  handleDeleteClick?: (item) => void;
  showActivityStatus?: boolean;
}

const PeopleCard: React.FC<PeopleCardProps> = ({ item, editing, handleDeleteClick, showActivityStatus = false }) => {
  const user = _.get(item, "user", _.get(item, "contact", {}));
  const canWrite = _.get(item, "canWrite", false)

  if (!user || _.isEmpty(user)) {
    return <></>;
  }

  const { firstName: name, email } = user;
  const image = _.get(user, 'icon.thumbnail', null);

  return (
    <div className={styles.peopleCard}>
      {editing && item.active && <div onClick={() => handleDeleteClick?.(item)} className={styles.closeIcon}>
        <i className='paperflite-close' />
      </div>}
      <div className={styles.avatar}>
        <img src={image ?? DefaultProfilePicture} alt={name} />
       {showActivityStatus && item.active && <div className={styles.statusContainer}>
          <div className={styles.status} />
        </div>}
      </div>
      <div className={styles.details}>
        <p className={styles.name}>
          <div style={{ maxWidth: item.active ? (canWrite ? 150 : 60) : 60 }} className={styles.truncatedText}>
            {name}
          </div>
          {item.active && !canWrite && <div className={styles.viewOnly}>view only</div>}
          {!item.active && <div className={styles.viewOnly}>no longer a participant</div>}
        </p>
        <p className={styles.email}>
          <div style={{ maxWidth: '150px' }} className={styles.truncatedText}>
            {email}
          </div>
        </p>
      </div>
    </div>
  )
}

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      height: active ? "auto" : 0,
      opacity: active ? 1 : 0,
      top: active ? 0 : 100,
      config: {
        duration: 100
      }
    },
    [active],
  );

  return (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {children}
    </animated.div>
  );
};

export default PeoplePage;